import { urlFor } from "@/utils/client";
import { getYouTubeId } from "@/utils/constants";
import { IHero, IImageWithCaption } from "@/utils/types";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import type { CSSProperties } from "react";
import { Button } from "./button";
import { SliderArrows } from "./sliderArrows";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <div className="h-[200px] md:h-[600px] w-full bg-gray-200" />,
});
export function Hero(
  props: IHero & {
    location?: string;
    date?: string;
    endDate?: string;
    timeOverride?: string;
    thumbnail?: IImageWithCaption;
    galleryAboveContent?: boolean;
  },
) {
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: (props.gallery?.items?.length ?? 0) > 1 ? true : false,
      slideChanged() {
        console.log("slide changed");
      },
    },
    [
      // add plugins here
    ],
  );

  const firstVideo = props.gallery?.items?.find((item) => item._type === "vwc");
  const preloadPosterUrl =
    firstVideo && "playbackId" in firstVideo
      ? `https://image.mux.com/${firstVideo.playbackId}/thumbnail.webp?time=0.1`
      : null;

  const gallerySection = props.gallery && (
    <>
      <div ref={sliderRef} className="keen-slider w-full">
        {(props.gallery.items ?? []).map((item, i) => {
          if (item._type === "iwc" && item.image) {
            return (
              <div key={i} className="keen-slider__slide">
                <Image
                  width={1920}
                  height={600}
                  sizes="100vw"
                  className="w-full h-[200px] md:h-[600px] object-cover"
                  src={urlFor(item.image).url()}
                  alt={item.caption ?? ""}
                />
              </div>
            );
          } else if (item._type === "vwc" && item.playbackId) {
            const posterUrl = `https://image.mux.com/${item.playbackId}/thumbnail.webp?time=0.1`;
            return (
              <div key={i} className="keen-slider__slide">
                <div className="h-[200px] md:h-[600px] w-full relative">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={posterUrl}
                    alt={item.caption ?? ""}
                    fetchPriority={i === 0 ? "high" : undefined}
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  />
                  <MuxPlayer
                    streamType="on-demand"
                    playbackId={item.playbackId}
                    loop={!!item.loop}
                    autoPlay={!!item.autoplay}
                    muted={!!item.autoplay}
                    playsInline
                    thumbnailTime={0.1}
                    disableCookies
                    preload="auto"
                    minResolution="1080p"
                    maxResolution="1080p"
                    renditionOrder="desc"
                    className="w-full h-full relative z-10"
                    style={
                      {
                        ["--media-object-fit" as never]: "cover",
                        ...(item.hideControls
                          ? { ["--controls" as never]: "none" }
                          : {}),
                      } as CSSProperties
                    }
                    title={item.caption ?? ""}
                  />
                </div>
              </div>
            );
          } else if (item._type === "ywc" && item.url) {
            const videoId = getYouTubeId(item.url);
            if (!videoId) return null;
            const ytSearch = new URLSearchParams({ rel: "0" });
            if (item.autoplay) {
              ytSearch.set("autoplay", "1");
              ytSearch.set("mute", "1");
            }
            if (item.loop) {
              ytSearch.set("loop", "1");
              ytSearch.set("playlist", videoId);
            }
            if (item.hideControls) {
              ytSearch.set("controls", "0");
              ytSearch.set("modestbranding", "1");
              ytSearch.set("showinfo", "0");
            }
            const ytParams = `?${ytSearch.toString()}`;
            return (
              <div key={i} className="keen-slider__slide">
                <div className="h-[200px] md:h-[600px] relative w-full">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${videoId}${ytParams}`}
                    title={item.caption ?? "YouTube video"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
      {(props.gallery.items?.length ?? 0) > 1 && (
        <SliderArrows
          onPrev={() => instanceRef.current?.prev()}
          onNext={() => instanceRef.current?.next()}
        />
      )}
    </>
  );

  const contentSection = (
    <div
      className={`${
        props.galleryAboveContent
          ? props.description
            ? "pt-4 pb-8 md:pt-8 md:pb-16"
            : "pt-4 md:pt-8"
          : props.description
            ? "py-8 md:py-16"
            : "pt-8 md:pt-16"
      } flex flex-col gap-4 md:gap-8`}
    >
      {props.thumbnail?.image && (
        <Image
          className="aspect-[4/3] h-full w-full lg:w-1/2 m-auto object-cover "
          width={800}
          height={600}
          sizes="(max-width: 1024px) 100vw, 50vw"
          alt={props.thumbnail?.caption ?? ""}
          src={urlFor(props.thumbnail.image).url()}
        />
      )}
      <h1 className="text-center text-4xl md:text-6xl lg:text-7xl m-auto">
        {props.title}
      </h1>
      {(props.location || props.date) && (
        <div className="flex items-center text-center justify-center flex-col md:flex-row gap-4">
          {props.location && (
            <p className="flex flex-col md:flex-row items-center gap-2">
              <Image
                className="invert"
                src={"/location.svg"}
                width={24}
                height={24}
                alt="location"
              />
              {props.location}
            </p>
          )}
          {props.date && (
            <p className="flex items-center gap-2">
              <Image
                className="invert"
                src={"/date.svg"}
                width={24}
                height={24}
                alt="date"
              />
              {format(new Date(props.date), "d MMMM yyyy", { locale: nl })}
            </p>
          )}
          {props.date && (
            <p className="flex items-center gap-2">
              <Image
                className="invert"
                src={"/time.svg"}
                width={24}
                height={24}
                alt="time"
              />
              {props.timeOverride
                ? props.timeOverride
                : `${format(new Date(props.date), "HH:mm", {
                    locale: nl,
                  })} - ${format(
                    new Date(props.endDate ? props.endDate : props.date),
                    "HH:mm",
                    { locale: nl },
                  )}`}
            </p>
          )}
        </div>
      )}
      {props.description && (
        <p className="text-center m-auto opacity-80">{props.description}</p>
      )}
      {props.buttons && (
        <div className="flex justify-center gap-4">
          {props.buttons.map((button, i) => {
            return (
              <Button
                key={i}
                backgroundColor={props.backgroundColor}
                accentColor={props.accentColor}
                link={button.link}
                label={button.label}
              />
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col w-full gap-4 md:gap-8">
      {preloadPosterUrl && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={preloadPosterUrl}
            fetchPriority="high"
          />
        </Head>
      )}
      {props.galleryAboveContent ? (
        <>
          {gallerySection}
          <div className="component-padding w-full">{contentSection}</div>
        </>
      ) : (
        <>
          <div className="component-padding w-full">{contentSection}</div>
          {gallerySection}
        </>
      )}
    </div>
  );
}
